import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AccordionName } from '../../../../UIStateSlice';
import Placeholder from './Placeholder';
import Scrollable from '../../../../components/scrollable/Scrollable';
import MessageBox from './MessageBox';
import { useAttributeSearch } from '../../../hooks/useAttributeSearch';
import useAppState from '../../../../AppStore';
import AccordionToggle from '../../../../components/accordion/AccordionToggle';
import { TopicLayerAttributeSearchResultsAccordionItem } from './TopicLayerAttributeSearchResultsAccordionItem';
import useDisplaySize from '../../../../useDisplaySize';

const formatAttributeValues = (attribute: any, value: any) => {
  if (attribute.dataTyp === 'Date' && typeof value === 'number') {
    return new Date(value).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
  return value;
};
interface TopicLayerAttributeSearchResultsProps {
  topicId: string;
}

const TopicLayerAttributeSearchResults = ({
  topicId,
}: TopicLayerAttributeSearchResultsProps) => {
  const { t } = useTranslation('agisviewer');
  const setAllAccordionItems = useAppState((state) => state.setAllAccordionItems);
  const removeAllAccordionItems = useAppState((state) => state.removeAllAccordionItems);
  const { isSmallScreen } = useDisplaySize();

  const {
    attributesQuery,
    metadataQuery,
    sublayerId,
    selectedFeatureId,
    setSelectedFeature,
    highlightedFeatureId,
    setHighlightedFeature,
  } = useAttributeSearch(topicId);

  const { data: attributesData } = attributesQuery;
  const { data: metadataData } = metadataQuery;

  const queryableAttributes = useMemo(() => {
    if (sublayerId === undefined) return {};
    return metadataData?.[sublayerId]?.queryableAttributes;
  }, [metadataData, sublayerId]);

  const features = useMemo(() => {
    removeAllAccordionItems(AccordionName.ATTRIBUTESEARCH_CONFIGURATION);
    if (!attributesData?.features || !queryableAttributes) {
      return [];
    }

    const queryableFieldnames = new Map(
      queryableAttributes.map((attribute: any) => [attribute.bezeichnung, attribute]),
    );

    return attributesData.features.map(({ properties, ...rest }: any) => {
      const filteredProperties = Object.fromEntries(
        Object.entries(properties)
          .filter(([key]) => queryableFieldnames.has(key))
          .map(([key, value]) => {
            const attribute = queryableFieldnames.get(key)!;
            return [key, formatAttributeValues(attribute, value)];
          }),
      );
      return { ...rest, properties: filteredProperties };
    });
  }, [attributesData, queryableAttributes]);

  const getDisplayAttributeName = useCallback(
    (name: string): string => {
      if (!queryableAttributes) return name;
      const aliasObj = queryableAttributes.find((item: any) => item.bezeichnung === name);
      return aliasObj?.anzeigename || aliasObj?.aliasname || name;
    },
    [queryableAttributes],
  );

  const onToggleAccordionItems = (expanded: boolean) => {
    const ids = features.map((feature: any) => String(feature.id)) || [];
    setAllAccordionItems(AccordionName.ATTRIBUTESEARCH_CONFIGURATION, ids, expanded);
  };

  const handleOnHighlightedFeature = (id?: number) => {
    setHighlightedFeature(id);
  };

  if (attributesQuery.isLoading || metadataQuery.isLoading) {
    return (
      <div className="tooldialog__wrapper-content">
        <Placeholder />{' '}
      </div>
    );
  }

  const noData = !attributesData || !metadataData || sublayerId === undefined;

  if (attributesQuery.isError || noData) {
    let errorMessage = t('topic_attribute_search_default_error');
    if (attributesQuery.isError) {
      const errorMessages: Record<string, string> = {
        maxQueryCountError: t(
          'topic_attribute_search_maxQueryCount',
          attributesQuery.error?.cause as {
            queryCount: number;
            maxQueryCount: number;
          },
        ),
        queryCountZeroError: t('topic_attribute_search_queryCountZero'),
      };
      errorMessage = errorMessages[attributesQuery.error.message] || errorMessage;
    }
    return (
      <div className="tooldialog__wrapper-content">
        <MessageBox type="negative" message={errorMessage} />{' '}
      </div>
    );
  }

  const { displayFieldName } = attributesData;

  return (
    <Scrollable noScroll={isSmallScreen} additionalClassName="search-query__list">
      <AccordionToggle
        onOpen={() => onToggleAccordionItems(true)}
        onClose={() => onToggleAccordionItems(false)}
        tooltipOpen={t('attribute_search_accordion_expand_all_tooltip')}
        tooltipClose={t('attribute_search_accordion_collapse_all_tooltip')}
      />

      {features.map((feature: any) => (
        <TopicLayerAttributeSearchResultsAccordionItem
          key={feature.id?.toString()}
          feature={feature}
          isSelected={selectedFeatureId === feature.id}
          isHighlighted={highlightedFeatureId === feature.id}
          setHighlightedFeature={handleOnHighlightedFeature}
          setSelectedFeature={setSelectedFeature}
          title={
            feature.properties[displayFieldName] ?? Object.values(feature.properties)[0]
          }
          dataListItems={Object.entries(feature.properties).map(([key, value]) => ({
            key: getDisplayAttributeName(key),
            value: <span>{value as string}</span>,
          }))}
        />
      ))}
    </Scrollable>
  );
};

export default TopicLayerAttributeSearchResults;
